/*
 * DEV-15069 — pre-lander redesign, Intelligems variant.
 *
 * Loaded on the pre-lander template for BOTH test groups: the CTA tracking has to fire the same
 * event name on the control hero and on the variant CTAs, so it cannot live inside the variant
 * markup. The interactive components only bind if their elements exist, so the control group
 * simply gets the tracking and nothing else.
 */

const LP_EVENT = 'prelander_primary_cta_click';
const LP_CONTROL_CTA = '.new_lander_btn a';
const LP_DEADLINE_KEY = 'lp15069_deadline';

/**
 * Countdown bar.
 *
 * Persists the deadline (not the remaining time) in sessionStorage so the timer survives reloads
 * and in-session navigation instead of restarting on every pageview. A new browser session starts
 * a fresh countdown, which is what "starts when the visitor's session starts" means. On reaching
 * zero the deadline is pushed forward by one full duration and counting continues.
 */
class LpCountdown extends HTMLElement {
  connectedCallback() {
    this.durationMs = (Number(this.dataset.duration) || 7200) * 1000;
    this.hours = this.querySelector('[data-lp-hours]');
    this.minutes = this.querySelector('[data-lp-minutes]');
    this.seconds = this.querySelector('[data-lp-seconds]');

    if (!this.hours || !this.minutes || !this.seconds) return;

    this.tick = this.tick.bind(this);
    this.tick();
    this.timer = setInterval(this.tick, 1000);
  }

  disconnectedCallback() {
    clearInterval(this.timer);
  }

  /** Reads the stored deadline, rolling it forward when missing, invalid or already passed. */
  getDeadline() {
    const now = Date.now();
    let deadline = Number(this.readStored());

    if (!Number.isFinite(deadline) || deadline <= now) {
      deadline = now + this.durationMs;
      this.writeStored(deadline);
    }

    return deadline;
  }

  readStored() {
    try {
      return sessionStorage.getItem(LP_DEADLINE_KEY);
    } catch {
      return this.fallbackDeadline ?? null;
    }
  }

  writeStored(value) {
    this.fallbackDeadline = value;
    try {
      sessionStorage.setItem(LP_DEADLINE_KEY, String(value));
    } catch {
      /* Private browsing or storage disabled — the in-memory fallback keeps the tab consistent. */
    }
  }

  tick() {
    const remaining = Math.max(0, this.getDeadline() - Date.now());
    const total = Math.floor(remaining / 1000);

    this.hours.textContent = LpCountdown.pad(Math.floor(total / 3600));
    this.minutes.textContent = LpCountdown.pad(Math.floor((total % 3600) / 60));
    this.seconds.textContent = LpCountdown.pad(total % 60);
  }

  static pad(value) {
    return String(value).padStart(2, '0');
  }
}

/**
 * Scrolling ticker.
 *
 * The marquee is two identical halves translated by exactly one half, so the loop is seamless as
 * long as one half is at least as wide as the viewport. Five items are not: the half measures around
 * 750px, so on any desktop the translate pulled the end of the track into view and left a blank
 * stretch behind it. This clones the item set into both halves until one covers the viewport, and
 * scales the animation duration with the distance so the marquee keeps a constant speed instead of
 * getting faster the more it has to cover.
 *
 * Clones are aria-hidden: only the first half's original items are announced.
 */
const LP_TICKER_SPEED = 30; // px per second

class LpTicker extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-lp-ticker-track]');
    this.lists = Array.from(this.querySelectorAll('[data-lp-ticker-list]'));
    if (!this.track || this.lists.length < 2) return;

    this.baseItems = this.lists.map((list) => Array.from(list.children).map((item) => item.cloneNode(true)));
    if (!this.baseItems[0].length) return;

    this.onResize = () => {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => this.layout(), 150);
    };

    this.layout();
    this.observer = new ResizeObserver(this.onResize);
    this.observer.observe(this);
  }

  disconnectedCallback() {
    clearTimeout(this.resizeTimer);
    this.observer?.disconnect();
  }

  layout() {
    const target = this.offsetWidth;
    if (!target) return;

    this.lists.forEach((list, index) => {
      list.replaceChildren(...this.baseItems[index].map((item) => item.cloneNode(true)));
    });

    // Bounded so a zero-width measurement — a hidden section, a font still loading — cannot spin.
    for (let pass = 0; pass < 12 && this.lists[0].getBoundingClientRect().width < target; pass += 1) {
      this.lists.forEach((list, index) => {
        this.baseItems[index].forEach((item) => {
          const clone = item.cloneNode(true);
          clone.setAttribute('aria-hidden', 'true');
          list.appendChild(clone);
        });
      });
    }

    const distance = this.lists[0].getBoundingClientRect().width;
    this.track.style.setProperty('--lpv-ticker-duration', `${Math.round(distance / LP_TICKER_SPEED)}s`);
  }
}

/**
 * Scroll-snap carousel used by the UGC and reviews sections.
 *
 * The track is a native overflow-x scroller, so touch swiping and keyboard scrolling work without
 * JS. This only adds the arrow buttons and the dot indicators, and keeps both in sync with
 * whatever the user actually scrolled to.
 */
class LpCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-lp-track]');
    if (!this.track) return;

    this.slides = Array.from(this.track.children);
    this.prev = this.querySelector('[data-lp-prev]');
    this.next = this.querySelector('[data-lp-next]');
    this.dotsHost = this.querySelector('[data-lp-dots]');

    this.onPrev = () => this.scrollToIndex(this.activeIndex() - 1);
    this.onNext = () => this.scrollToIndex(this.activeIndex() + 1);
    this.onScroll = () => this.sync();

    this.prev?.addEventListener('click', this.onPrev);
    this.next?.addEventListener('click', this.onNext);
    this.track.addEventListener('scroll', this.onScroll, { passive: true });

    this.buildDots();
    this.sync();
  }

  disconnectedCallback() {
    this.prev?.removeEventListener('click', this.onPrev);
    this.next?.removeEventListener('click', this.onNext);
    this.track?.removeEventListener('scroll', this.onScroll);
  }

  buildDots() {
    if (!this.dotsHost || this.slides.length < 2) return;

    this.dots = this.slides.map((_, index) => {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'lpv-carousel__dot';
      dot.setAttribute('role', 'tab');
      dot.setAttribute('aria-label', `Go to slide ${index + 1} of ${this.slides.length}`);
      dot.addEventListener('click', () => this.scrollToIndex(index));
      this.dotsHost.appendChild(dot);
      return dot;
    });
  }

  activeIndex() {
    const { scrollLeft } = this.track;
    let closest = 0;
    let smallest = Infinity;

    this.slides.forEach((slide, index) => {
      const distance = Math.abs(slide.offsetLeft - this.track.offsetLeft - scrollLeft);
      if (distance < smallest) {
        smallest = distance;
        closest = index;
      }
    });

    return closest;
  }

  scrollToIndex(index) {
    const target = this.slides[Math.min(Math.max(index, 0), this.slides.length - 1)];
    if (!target) return;

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.track.scrollTo({
      left: target.offsetLeft - this.track.offsetLeft,
      behavior: reduced ? 'auto' : 'smooth',
    });
  }

  sync() {
    const index = this.activeIndex();
    const atStart = this.track.scrollLeft <= 1;
    const atEnd = this.track.scrollLeft + this.track.clientWidth >= this.track.scrollWidth - 1;

    if (this.prev) this.prev.disabled = atStart;
    if (this.next) this.next.disabled = atEnd;

    this.dots?.forEach((dot, dotIndex) => {
      dot.setAttribute('aria-selected', String(dotIndex === index));
    });
  }
}

/**
 * FAQ accordion.
 *
 * The markup is native <details>, so it is already keyboard accessible and works without JS.
 * This only enforces the single-open behaviour from the design.
 */
class LpFaq extends HTMLElement {
  connectedCallback() {
    this.items = Array.from(this.querySelectorAll('details'));
    this.onToggle = (event) => {
      if (!event.target.open) return;
      this.items.forEach((item) => {
        if (item !== event.target) item.open = false;
      });
    };

    this.items.forEach((item) => item.addEventListener('toggle', this.onToggle));
  }

  disconnectedCallback() {
    this.items?.forEach((item) => item.removeEventListener('toggle', this.onToggle));
  }
}

/**
 * UGC video tiles (homepage parity).
 *
 * Mirrors the Replo player on the homepage UGC carousel: poster with a centred play disc, tap
 * plays WITH sound, a sound toggle sits bottom-right, and starting one tile pauses every other.
 * No autoplay, no loop; on ended the tile resets to its poster.
 *
 * Everything is delegated off document instead of bound per tile, so it keeps working when the
 * theme editor re-renders the section. Media events do not bubble, hence the capture listeners.
 */
const LP_VIDEO_TILE = '[data-lp-video]';

function onUgcTileClick(event) {
  const tile = event.target.closest(LP_VIDEO_TILE);
  const video = tile?.querySelector('video');
  if (!video) return;

  const mute = event.target.closest('[data-lp-mute]');
  if (mute) {
    video.muted = !video.muted;
    mute.classList.toggle('is-muted', video.muted);
    mute.setAttribute('aria-pressed', String(video.muted));
    mute.setAttribute('aria-label', video.muted ? 'Unmute video' : 'Mute video');
    return;
  }

  // The play disc and the video surface both toggle playback, like the homepage player.
  if (video.paused) {
    video.play()?.catch(() => {});
  } else {
    video.pause();
  }
}

function onUgcVideoPlay(event) {
  const video = event.target;
  const tile = video.closest?.(LP_VIDEO_TILE);
  if (!tile) return;

  document.querySelectorAll(`${LP_VIDEO_TILE} video`).forEach((other) => {
    if (other !== video && !other.paused) other.pause();
  });

  tile.classList.add('is-playing');
  tile.querySelector('[data-lp-play]')?.setAttribute('aria-label', 'Pause video');
}

function onUgcVideoPause(event) {
  const tile = event.target.closest?.(LP_VIDEO_TILE);
  if (!tile) return;

  tile.classList.remove('is-playing');
  tile.querySelector('[data-lp-play]')?.setAttribute('aria-label', 'Play video');
}

function onUgcVideoEnded(event) {
  const video = event.target;
  if (!video.closest?.(LP_VIDEO_TILE)) return;

  // load() rewinds AND puts the poster frame back, which currentTime = 0 would not.
  video.load();
}

/**
 * Fires the Intelligems custom event for any primary CTA that leads to the collection page.
 *
 * Signature confirmed against the loaded bundle: window.igEvents is an IgEvents instance whose
 * push() takes { event, properties? }. `type` and `source` are deliberately omitted — the
 * bundle's validator checks them against each other's enum, so passing either logs a spurious
 * warning, and both default correctly.
 *
 * Before Intelligems initialises, window.igEvents is a plain array it later drains, so the same
 * push() call is correct whether or not the bundle has booted.
 */
function trackCtaClick(target) {
  const queue = (window.igEvents = window.igEvents || []);

  try {
    queue.push({
      event: LP_EVENT,
      properties: { label: target.textContent.trim().slice(0, 80) },
    });
  } catch (error) {
    console.warn(`${LP_EVENT} could not be sent`, error);
  }
}

function onDocumentClick(event) {
  const target = event.target.closest(`[data-lp-cta], ${LP_CONTROL_CTA}`);
  if (target) trackCtaClick(target);
}

/*
 * Loaded as a module, so the browser evaluates this file once per document no matter how many
 * sections request it. The flag is belt-and-braces: if the tag is ever changed back to a classic
 * script, this still prevents the delegated listener being registered twice and firing the
 * tracking event twice per click.
 */
if (!window.__lpPrelanderInit) {
  window.__lpPrelanderInit = true;

  document.addEventListener('click', onDocumentClick);
  document.addEventListener('click', onUgcTileClick);
  document.addEventListener('play', onUgcVideoPlay, true);
  document.addEventListener('pause', onUgcVideoPause, true);
  document.addEventListener('ended', onUgcVideoEnded, true);

  customElements.define('lp-countdown', LpCountdown);
  customElements.define('lp-ticker', LpTicker);
  customElements.define('lp-carousel', LpCarousel);
  customElements.define('lp-faq', LpFaq);
}
